import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { navigate } from 'hooks/useHookRouterCompat';
import Keyword from 'data/Keyword';
import Brand, * as Br from 'data/Brand';
import Vendor from 'data/Vendor';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import ConfirmModal from 'components/UI/Modal';
import Page from 'components/UI/Page';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { Color } from 'lib/styles';
import { BorderRadius } from "styles/themeBorderRadius";
import { getStash } from 'lib/stash';
import { useAPI } from 'lib/API';
import {
    createBrand,
    loadBrand,
    loadBrands,
    saveBrand
} from 'requests/brand';
import { loadKeywords } from 'requests/keyword';
import { Space } from 'styles/themeSpaces';
import { Main, Background } from 'styles/themeColors';
import Tabs from 'components/UI/Tabs';
import AutomationSettingTab from './AutomationSettingTab';
import { getBrandTabs } from 'constants/tabs';
import BrandConfigurationTab from './BrandConfigurationTab';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { mapBrandVendorToBrand, validateBrandData } from 'utils/brand';
import FeatureFlag, { FeatureFlagValues } from 'data/FeatureFlag';
import { loadVendor } from 'requests/vendor';
import BrandConfigurationTabV2 from './BrandConfigurationTabV2';
import { isOfmpAdmin, isVendorAdmin } from 'data/User';
import { useAuth } from 'lib/Auth';
import { BRAND_CONSTANTS } from 'constants/brands';
import { PermissionCode } from 'data/UserPermissions';
import { useReadPermission } from 'hooks/useUserRoles';
import { isVendorOnlyByBusinessUnit } from 'utils/role';
import { BusinessUnit } from 'data/BusinessUnit';

const CancelButton = styled(Button)`
  margin-right: 5px;
  color: ${Color.black} !important;
  background-color: ${Background.white};
  border: 1px solid ${Color.veryLightGray} !important;
`;

const SaveButton = styled(Button)`
  padding: 10px;
  border-radius: ${BorderRadius.m};
  background-color: ${Main.brandDark};
  border-color: ${Main.disabled} !important;
`;

const Span = styled.span`
  margin: ${Space.spBase} 0;
`;

export interface Props {
    brandId?: string;
    selectedVendor: Vendor;
    selectedBusinessUnit?: BusinessUnit;
    vendorsList?: Vendor[];
    featureFlags: FeatureFlag;
    updateVendor: (name: string, brandId?: number) => void;
};

const BrandDetailsV2 = ({ brandId, selectedVendor, selectedBusinessUnit, updateVendor, featureFlags }: Props) => {
    // Get quwery params

    const isUserPermissionsEnabled = featureFlags?.businessUnit?.enabled ?? false;
    const BRAND_TABS = getBrandTabs(isUserPermissionsEnabled);

    const { user } = useAuth();

    const [keywordsList, setKeywordsList] = useState<Keyword[]>([]);
    const [brand, setBrand] = useState<Brand>({ id: 0, ...Br.empty })
    const [keywordsTags, setKeywordsTags] = useState<string[]>([]);
    const [showBrandCancelModal, setShowBrandCancelModal] = useState(false);
    const [brandTabs, setBrandTabs] = useState(BRAND_TABS);
    const [vendor, setVendor] = useState<Vendor>({ ...selectedVendor });
    const [errors, setErrors] = useState<any>({});

    const vendorReq = useAPI({ deferFn: loadVendor });
    const { readOnly } = useReadPermission(PermissionCode.BRANDS_VIEW_CREATE_DELETE);

    useEffect(() => {
        if (selectedVendor) {
            vendorReq.run(selectedVendor?.id);
        }
    }, [selectedVendor]);

    useEffect(() => {
        if (vendorReq.data) {
            setVendor(vendorReq.data);
            setBrandTabs((vendorReq.data.isMaterialApiEnabled || (featureFlags?.salesOrder && featureFlags?.salesOrder.enabled && vendorReq.data.isSalesApiEnabled)) ? BRAND_TABS : [BRAND_TABS[0]]);
        }
    }, [vendorReq.data]);

    useEffect(() => {
        if (readOnly) {
            navigate('/brands');
        }
    }, []);

    const brandReq = useAPI<Brand>({
        deferFn: loadBrand,
        onResolve: (result: any) => {
            const brandVendor = mapBrandVendorToBrand(result);
            setBrand({ ...result, ...brandVendor, selectedBrandsIds: result.brands?.map((brand: Brand) => brand.id) });
            updateVendor(result.vendor![0].name!, result?.id);
        },
        onReject: err => {
            renderToast(toast.TYPE.ERROR, err.message);
        }
    });

    const keywordsReq = useAPI({
        promiseFn: loadKeywords,
        onResolve: (result) => {
            setKeywordsList(result);
        },
        onReject: err => {
            renderToast(toast.TYPE.ERROR, err.message);
        }
    });

    const updateBrandReq = useAPI({
        deferFn: saveBrand,
        onResolve: () => {
            renderToast(toast.TYPE.SUCCESS, BRAND_CONSTANTS.BRAND_UPDATED);
            navigate(previousLibraryPath);
        },
        onReject: err => {
            renderToast(toast.TYPE.ERROR, err.message);
        },
    });

    const createReq = useAPI({
        deferFn: createBrand,
        onResolve: () => {
            renderToast(toast.TYPE.SUCCESS, BRAND_CONSTANTS.BRAND_CREATED);
            navigate(previousLibraryPath);
        },
        onReject: err => {
            renderToast(toast.TYPE.ERROR, err.message);
        },
    });

    
    const isVendorOnlyUser = isVendorOnlyByBusinessUnit(user!, selectedBusinessUnit);
    const hasEditRights = !isVendorOnlyUser;
    
    const disabled = !!(
        (!vendor?.isSlbVendor && brand?.isMaterialApiEnabled) && !brand.authenticationUrl ||
        (!vendor?.isSlbVendor && brand?.isMaterialApiEnabled) && !brand.consumerSecret ||
        (!vendor?.isSlbVendor && brand?.isMaterialApiEnabled) && !brand.consumerKey ||
        (!vendor?.isSlbVendor && brand?.isMaterialApiEnabled) && !brand.baseUrl ||
        updateBrandReq.isLoading ||
        createReq.isLoading ||
        !hasEditRights
    );

    useEffect(() => {
        if (brandId && vendorReq.data) {
            brandReq.run(brandId);
        }
    }, [vendorReq.data, brandId]);


    const [brands, setBrands] = useState<Brand[]>([]);
    const brandsReq = useAPI({
        deferFn: loadBrands,
        onResolve: (result) => {
            setBrands(result);
        }
    });

    useEffect(() => {
        brandsReq.run(selectedVendor ? selectedVendor.id : null);
    }, [selectedVendor]);

    useEffect(() => {
        if (selectedVendor) {
            setBrand({ ...brand, vendor: selectedVendor });
        }
    }, [selectedVendor]);

    useEffect(() => {
        if (brand) {
            setKeywordsTags(brand.keywords);
        }
    }, [brand && brand.keywords]);

    const saveClick = () => {
        if (!selectedVendor) {
            renderToast(toast.TYPE.INFO, BRAND_CONSTANTS.PLEASE_SELECT_VENDOR);
            return;
        }

        const { isValid, errors } = validateBrandData(brand);

        if (!isValid) {
            setErrors(errors);
            return;
        }

        if (brand.id === 0) {
            createReq.run(brand, selectedVendor.id);
            setErrors({});
        } else {
            updateBrandReq.run(brand, selectedVendor.id);
            setErrors({});
        }
    };

    const previousLibraryPath: string = getStash('brands') || '/brands';

    const onConfirmCancelBrandModalClick = () => {
        setShowBrandCancelModal(false);
        navigate('/brands');
    };

    const confirmBrandCancelModal = (
        <ConfirmModal
            showModal={showBrandCancelModal}
            onClose={() => setShowBrandCancelModal(false)}
            onClick={onConfirmCancelBrandModalClick}
            title="Are you sure you want to exit?"
            primaryButtonText="Confirm"
            ghostButtonText="Back"
            primaryButtonColor={Main.brandDark}
            color={Main.black}
            borderColor={Main.disabled}

        >
            <FlexboxContainer flexDirection="column">
                <Span>
                    {BRAND_CONSTANTS.ANY_CHANGE_SAVE_DISCLAIMER}
                </Span>
            </FlexboxContainer>
        </ConfirmModal>
    )

    const isBrandsV2Enabled = featureFlags[FeatureFlagValues.BRANDS_V2]?.enabled;
    const BrandConfigurationComponent = isBrandsV2Enabled ? BrandConfigurationTabV2 : BrandConfigurationTab;

    return (

        brandReq.isLoading || keywordsReq.isLoading || brandsReq.isLoading || vendorReq.isLoading || createReq.isLoading || updateBrandReq.isLoading
            ? <LoadingIndicator center />
            :
            <Tabs
                theme='brandDark'
                margin='8px 24px 8px 0'
                height='36px !important'
                borderRadius='20px'
                tabPanelPadding='0 !important'
                tabs={brandTabs}
                activeTab={0}
                padding='15px 24px'
                className='brands-tabs'
                actionButtons={
                    <FlexboxContainer width="100%" border="none" alignItems='center'>
                        <div style={{ border: 'none', display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                            <CancelButton key="cancel" onClick={() => hasEditRights ? setShowBrandCancelModal(true) : navigate('/brands')}>
                                {BRAND_CONSTANTS.BACK}
                            </CancelButton>
                            {confirmBrandCancelModal}
                            <SaveButton key="save" onClick={saveClick} disabled={disabled}>
                                {BRAND_CONSTANTS.SAVE}
                            </SaveButton>
                        </div>
                    </FlexboxContainer>
                }
            >
                <BrandConfigurationComponent
                    brand={brand}
                    selectedVendor={vendor}
                    keywordsList={keywordsList}
                    updateVendor={updateVendor}
                    setBrand={setBrand}
                    brandsList={isBrandsV2Enabled ? brands : undefined}
                    errors={errors}
                    setErrors={setErrors}
                    hasEditRights={hasEditRights}
                    featureFlags={featureFlags}
                />
                <AutomationSettingTab
                    brand={brand}
                    selectedVendor={vendor}
                    featureFlags={featureFlags}
                    setBrand={setBrand}
                    hasEditRights={hasEditRights}
                />
            </Tabs>
    );
};

export default BrandDetailsV2;
